import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  CheckCircle2, AlertTriangle, RefreshCw, Trash2, Loader2,
  Database, Clock, ExternalLink, ChevronDown, ChevronRight, Sparkles,
} from "lucide-react";

/**
 * Pinterest API v5 connector page. Mirrors the X page UX:
 * OAuth-only connection, 1-per-hour manual sync, hybrid catalog preview.
 */
export default function ClientPinterestPage() {
  const { clientId } = useParams();
  const [catalog, setCatalog] = useState(null);
  const [config, setConfig] = useState({ configured: false });
  const [connection, setConnection] = useState({ connected: false });
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [cat, cfg, conn] = await Promise.all([
        api.get("/pinterest/field-catalog").then((r) => r.data).catch(() => null),
        api.get("/pinterest/config-status").then((r) => r.data).catch(() => ({ configured: false })),
        api.get(`/clients/${clientId}/pinterest/connection`).then((r) => r.data).catch(() => ({ connected: false })),
      ]);
      setCatalog(cat); setConfig(cfg); setConnection(conn);
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, [clientId]);

  const cooldownText = useMemo(() => {
    const t = connection.manual_sync_available_at;
    if (!t) return null;
    const diff = Math.max(0, Math.round(t - Date.now() / 1000));
    const m = Math.floor(diff / 60); const s = diff % 60;
    return `${m} min ${s.toString().padStart(2, "0")} s`;
  }, [connection.manual_sync_available_at]);

  const startConnect = async () => {
    setConnecting(true);
    try {
      const redirect = `${window.location.origin}/pinterest-oauth-callback`;
      const { data } = await api.post(`/clients/${clientId}/pinterest/oauth/start`,
        { redirect_uri: redirect });
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start Pinterest OAuth");
    } finally { setConnecting(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Pinterest?")) return;
    await api.delete(`/clients/${clientId}/pinterest/disconnect`);
    toast.success("Disconnected"); await refresh();
  };

  const runManualSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/pinterest/sync`, {});
      toast.success(`Synced · ${data.rows} rows · ${data.start_date} → ${data.end_date}`);
      setConfirmOpen(false); await refresh();
    } catch (e) {
      const status = e.response?.status;
      const msg = e.response?.data?.detail || "Sync failed";
      if (status === 429) { toast.warning(msg); } else { toast.error(msg); }
    } finally { setSyncing(false); }
  };

  if (loading) return <div className="p-8 text-sm text-zinc-500">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="pinterest-page">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 grid place-items-center rounded-sm bg-[#E60023] text-white font-display font-extrabold">
          P
        </div>
        <div>
          <div className="label-overline">Pinterest</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">Pinterest</h1>
          <p className="text-xs text-zinc-600 mt-1 max-w-2xl">
            Pull user account + pin / board analytics from Pinterest API v5.
            Scheduled sync runs daily; the manual sync is rate-limited to
            <b> 1 pull per client per hour</b>.
          </p>
        </div>
      </div>

      {!config.configured && (
        <Card className="rounded-sm border-amber-200 bg-amber-50" data-testid="pin-setup-banner">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
            <div className="text-[12px] text-amber-900 flex-1">
              <div className="font-semibold mb-0.5">Pinterest credentials not set yet</div>
              <div>
                Set <span className="font-mono">PINTEREST_APP_ID</span> +
                <span className="font-mono"> PINTEREST_APP_SECRET</span> in
                <span className="font-mono"> backend/.env</span> and restart the
                backend. Apply at{" "}
                <a href="https://developers.pinterest.com/apps/" target="_blank" rel="noreferrer"
                  className="underline">developers.pinterest.com/apps <ExternalLink className="inline h-3 w-3" /></a>{" "}
                — full guide at{" "}
                <a href="/docs/pinterest_setup.md" target="_blank" rel="noreferrer"
                  className="underline">docs/pinterest_setup.md</a>.
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Connection card */}
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-5 space-y-3">
          {connection.connected ? (
            <>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  <Badge className="rounded-sm bg-emerald-100 text-emerald-800 border-emerald-200">
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Connected
                  </Badge>
                  <div className="text-[11px] font-mono text-zinc-500">
                    {connection.last_sync_at
                      ? `Last sync: ${new Date(connection.last_sync_at).toLocaleString()}`
                      : "never synced"}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="rounded-sm bg-[#E60023] hover:bg-[#b8001c] text-white"
                    disabled={syncing}
                    onClick={() => setConfirmOpen(true)}
                    data-testid="pin-manual-sync">
                    {syncing
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                      : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                    Force sync now
                  </Button>
                  <Button size="sm" variant="ghost" className="rounded-sm text-red-600"
                    onClick={disconnect} data-testid="pin-disconnect">
                    <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Disconnect
                  </Button>
                </div>
              </div>
              {connection.last_error && (
                <div className="rounded-sm border border-red-200 bg-red-50 p-2.5 text-[11px] font-mono text-red-800">
                  {connection.last_error}
                </div>
              )}
              {cooldownText && (
                <div className="text-[11px] text-zinc-500 flex items-center gap-1"
                  data-testid="pin-cooldown">
                  <Clock className="h-3 w-3" /> Next manual sync available in {cooldownText}.
                </div>
              )}
            </>
          ) : (
            <div className="space-y-3">
              <div className="label-overline">Connect this client&apos;s Pinterest account</div>
              <p className="text-[12px] text-zinc-600">
                Sign in as the Pinterest user that owns or has access to the
                business account. We&apos;ll request{" "}
                <span className="font-mono">user_accounts:read · boards:read · pins:read · ads:read</span>.
              </p>
              <Button onClick={startConnect} disabled={connecting || !config.configured}
                className="rounded-sm bg-[#E60023] hover:bg-[#b8001c] text-white"
                data-testid="pin-connect">
                {connecting
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                  : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
                Connect with Pinterest
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Catalog preview */}
      {catalog && (
        <Card className="rounded-sm border-zinc-200" data-testid="pin-catalog">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center gap-2">
              <Database className="h-4 w-4 text-zinc-500" />
              <div className="label-overline">Available metrics</div>
            </div>
            {Object.entries(catalog).map(([section, v]) => (
              <CatalogSection key={section} section={section} v={v} showAdvanced={showAdvanced} />
            ))}
            <button type="button"
              onClick={() => setShowAdvanced((s) => !s)}
              className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-zinc-700 hover:text-[#E60023]"
              data-testid="pin-advanced-toggle">
              {showAdvanced ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
              Advanced groups
            </button>
          </CardContent>
        </Card>
      )}

      {/* Manual sync confirmation */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="rounded-sm max-w-md" data-testid="pin-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-bold text-2xl tracking-tight">
              Force a fresh Pinterest sync?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-zinc-700">
            <p>
              Pinterest scheduled-syncs run once per day. You can only run{" "}
              <b>one manual on-demand sync per client per hour</b>.
            </p>
            <p className="text-[12px] text-zinc-500">
              Pulls the latest user account + the last 30 days of account analytics.
            </p>
            {cooldownText && (
              <div className="rounded-sm border border-amber-200 bg-amber-50 p-2 text-[12px] text-amber-900">
                Next manual sync available in <b>{cooldownText}</b>.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm"
              onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button className="rounded-sm bg-[#E60023] hover:bg-[#b8001c] text-white"
              disabled={syncing} onClick={runManualSync}
              data-testid="pin-confirm-sync">
              {syncing
                ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
              Yes, sync now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CatalogSection({ section, v, showAdvanced }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-2">
        {v.label}
      </div>
      <div className="space-y-3">
        {(v.groups || []).map((g) => {
          if (g.advanced && !showAdvanced) return null;
          return (
            <div key={g.label}>
              <div className="text-[10px] font-mono text-zinc-400 mb-1.5 uppercase">
                {g.label}{g.advanced ? " · advanced" : ""}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1.5">
                {g.fields.map((f) => (
                  <label key={f.key}
                    className="flex items-center gap-2 p-2 rounded-sm border border-zinc-200 bg-white"
                    data-testid={`pin-field-${f.key}`}>
                    <Checkbox checked disabled />
                    <div className="min-w-0">
                      <div className="text-xs">{f.desc}</div>
                      <div className="font-mono text-[10px] text-zinc-400">{f.key}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
